import { TournamentConfig, MatchResult, CoffeeShop } from './types';

// Each round is labeled with its tournament name and, just for fun, the
// roast level that round "represents" -- gets darker as the field narrows.
export const ROUNDS_META = [
  { name: 'Round of 16', roast: 'Light Roast' },
  { name: 'Quarterfinals', roast: 'Medium Roast' },
  { name: 'Semifinals', roast: 'Dark Roast' },
  { name: 'Championship', roast: 'French Roast' },
];

export interface ResolvedMatch {
  matchId: string;
  shopAId: string | null;
  shopBId: string | null;
  result: MatchResult | null;
  // 1-indexed match numbers from the previous round that feed into this
  // match (e.g. [1, 2] means "winner of Match 1" / "winner of Match 2" from
  // the round to the left). Null for Round 1, where shops come straight
  // from round1Matchups.
  feederMatchNumbers: [number, number] | null;
  // Human-readable "waiting on" labels for the two slots, derived from the
  // feeder matches (e.g. "Winner of Coffee Code v. Bodhi Leaf Coffee
  // Traders"). Falls back to "Winner of Match N" when the feeder match's own
  // opponents aren't known yet. Null for Round 1.
  tbdLabels: [string, string] | null;
}

export function getMatchId(roundIndex: number, matchIndex: number): string {
  return `r${roundIndex + 1}m${matchIndex + 1}`;
}

function shopName(shops: CoffeeShop[], id: string | null): string | null {
  if (!id) return null;
  return shops.find((s) => s.id === id)?.name ?? null;
}

function feederLabel(feederMatch: ResolvedMatch, matchNumber: number, shops: CoffeeShop[]): string {
  const nameA = shopName(shops, feederMatch.shopAId);
  const nameB = shopName(shops, feederMatch.shopBId);
  if (nameA && nameB) {
    return `Winner of ${nameA} v. ${nameB}`;
  }
  return `Winner of Match ${matchNumber}`;
}

function resolveRound(
  roundIndex: number,
  config: TournamentConfig,
  previousRound?: ResolvedMatch[]
): ResolvedMatch[] {
  const matchCount = 8 / Math.pow(2, roundIndex); // 8, 4, 2, 1
  const matches: ResolvedMatch[] = [];

  for (let i = 0; i < matchCount; i++) {
    const matchId = getMatchId(roundIndex, i);
    let shopAId: string | null = null;
    let shopBId: string | null = null;
    let feederMatchNumbers: [number, number] | null = null;
    let tbdLabels: [string, string] | null = null;

    if (roundIndex === 0) {
      const pair = config.round1Matchups[i];
      shopAId = pair?.[0] ?? null;
      shopBId = pair?.[1] ?? null;
    } else if (previousRound) {
      const feederA = previousRound[i * 2];
      const feederB = previousRound[i * 2 + 1];
      shopAId = feederA?.result?.winnerId ?? null;
      shopBId = feederB?.result?.winnerId ?? null;
      feederMatchNumbers = [i * 2 + 1, i * 2 + 2];
      tbdLabels = [
        feederLabel(feederA, i * 2 + 1, config.shops),
        feederLabel(feederB, i * 2 + 2, config.shops),
      ];
    }

    matches.push({
      matchId,
      shopAId,
      shopBId,
      result: config.results[matchId] ?? null,
      feederMatchNumbers,
      tbdLabels,
    });
  }

  return matches;
}

export function resolveBracket(config: TournamentConfig): ResolvedMatch[][] {
  const rounds: ResolvedMatch[][] = [];
  for (let r = 0; r < ROUNDS_META.length; r++) {
    rounds.push(resolveRound(r, config, rounds[r - 1]));
  }
  return rounds;
}

export function getChampionId(config: TournamentConfig): string | null {
  const rounds = resolveBracket(config);
  const final = rounds[rounds.length - 1][0];
  return final?.result?.winnerId ?? null;
}
